'use client';

import { useEffect, useState, Suspense } from 'react';
import { useRouter } from 'next/navigation';
import BottomNav from '@/app/components/BottomNav';
import { format } from 'date-fns';

interface Transaction {
  id: number;
  transaction_id: string;
  amount_sent: number;
  amount_received: number;
  direction: string;
  exchange_rate: number;
  fee_amount: number;
  margin_percentage: number;
  source_account: string;
  destination_account: string;
  created_at: string;
  notes: string | null;
}

function HistoryContent() {
  const router = useRouter();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        console.log('[HISTORY] Fetching from URL:', `${process.env.NEXT_PUBLIC_API_URL}/api/history`);
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/history`);
        console.log('[HISTORY] Response status:', response.status, response.statusText);
        const data = await response.json();
        console.log('[HISTORY] Response data:', data);
        
        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch transactions');
        }
        
        // Convert string numbers to actual numbers
        const transactions = (data.data || []).map((tx: any) => ({
          ...tx,
          amount_sent: typeof tx.amount_sent === 'string' ? parseFloat(tx.amount_sent) : tx.amount_sent,
          amount_received: typeof tx.amount_received === 'string' ? parseFloat(tx.amount_received) : tx.amount_received,
          exchange_rate: typeof tx.exchange_rate === 'string' ? parseFloat(tx.exchange_rate) : tx.exchange_rate,
          fee_amount: typeof tx.fee_amount === 'string' ? parseFloat(tx.fee_amount) : tx.fee_amount,
          fee_percentage: typeof tx.fee_percentage === 'string' ? parseFloat(tx.fee_percentage) : tx.fee_percentage,
          margin_percentage: typeof tx.margin_percentage === 'string' ? parseFloat(tx.margin_percentage) : tx.margin_percentage,
        }));
        setTransactions(transactions);
      } catch (err) {
        let errorMessage = 'Failed to load transactions';
        if (err instanceof Error) {
          errorMessage = err.message;
        } else if (typeof err === 'string') {
          errorMessage = err;
        } else {
          errorMessage = JSON.stringify(err);
        }
        console.error('[HISTORY] Error fetching transactions:', errorMessage);
        setError(errorMessage);
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  // Filter transactions based on search query (name or phone number)
  const filteredTransactions = transactions.filter((tx) => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const nameMatch = tx.notes?.toLowerCase().includes(searchLower);
    const phoneMatch = tx.source_account?.toLowerCase().includes(searchLower);
    const transactionIdMatch = tx.transaction_id?.toLowerCase().includes(searchLower);
    return nameMatch || phoneMatch || transactionIdMatch;
  });

  // Group transactions by date
  const groupedTransactions = filteredTransactions.reduce((acc, tx) => {
    const date = format(new Date(tx.created_at), 'yyyy-MM-dd');
    if (!acc[date]) acc[date] = [];
    acc[date].push(tx);
    return acc;
  }, {} as Record<string, Transaction[]>);

  // Sort dates in descending order
  const sortedDates = Object.keys(groupedTransactions).sort().reverse();

  return (
    <>
      <div className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white min-h-screen w-screen flex flex-col">
        <div className="relative z-10 flex-1 overflow-y-auto pt-6 pb-40 px-4 sm:px-6">
          {/* Header */}
          <header className="w-full pb-6 mb-6 sticky top-0 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 z-20">
            <div className="mb-4">
              <h1 className="text-3xl font-bold tracking-tight">Transaction History</h1>
              <p className="text-sm text-slate-400 font-medium mt-1">All your transactions in one place</p>
            </div>
            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name, phone number or notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500/50 focus:bg-slate-800/70 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
          </header>

          <main className="w-full">
            {loading && (
              <div className="px-4 sm:px-6 py-12 text-center">
                <div className="inline-block">
                  <div className="w-10 h-10 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
                </div>
                <p className="text-slate-400 mt-4">Loading transactions...</p>
              </div>
            )}

            {error && (
              <div className="px-4 sm:px-6 py-8">
                <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4">
                  <p className="text-red-300 text-sm">Error: {error}</p>
                </div>
              </div>
            )}

            {!loading && !error && filteredTransactions.length === 0 && (
              <div className="px-4 sm:px-6 py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-800/50 mx-auto flex items-center justify-center mb-4">
                  <i className="ph-bold ph-receipt text-2xl text-slate-400"></i>
                </div>
                <p className="text-slate-400">{searchQuery ? 'No matching transactions' : 'No transactions yet'}</p>
                <p className="text-slate-500 text-sm mt-1">{searchQuery ? 'Try a different search' : 'Your transaction history will appear here'}</p>
              </div>
            )}

            {!loading && !error && filteredTransactions.length > 0 && (
              <section className="space-y-6 pb-4">
                {sortedDates.map((date) => (
                  <div key={date}>
                    <h3 className="text-[11px] font-display font-medium text-slate-500 uppercase tracking-widest mb-3 pl-1">
                      {format(new Date(date), 'EEEE, MMMM d, yyyy')}
                    </h3>
                    <div className="space-y-3">
                      {groupedTransactions[date].map((tx) => {
                        const handleTransactionClick = () => {
                          const params = new URLSearchParams({
                            transactionId: tx.transaction_id,
                            amountSend: tx.amount_sent.toString(),
                            amountReceived: tx.amount_received.toString(),
                            direction: tx.direction,
                            exchangeRate: tx.exchange_rate.toString(),
                            feeAmount: tx.fee_amount.toString(),
                            paymentNumber: tx.source_account,
                            notes: tx.notes || '',
                            createdAt: tx.created_at,
                          });
                          router.push(`/receipt?${params.toString()}`);
                        };

                        return (
                          <div
                            key={tx.id}
                            onClick={handleTransactionClick}
                            className="glass-panel p-4 rounded-2xl flex items-center justify-between group active:scale-[0.98] transition-all cursor-pointer hover:bg-white/5"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-11 h-11 rounded-full border border-red-500/30 bg-red-500/10 flex items-center justify-center transition-all">
                                <i className="ph-bold ph-arrow-up-right text-lg text-red-400"></i>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold font-display text-white">
                                  Money Sent
                                </h4>
                                <p className="text-xs text-slate-400 mt-0.5">{format(new Date(tx.created_at), 'MMM d, yyyy hh:mm:ss a')}</p>
                                {tx.notes && <p className="text-xs text-slate-500 mt-1">{tx.notes}</p>}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-display font-semibold text-red-400">
                                -{tx.amount_sent.toFixed(2)}
                              </div>
                              <div className="flex items-center justify-end gap-1.5 mt-0.5">
                                <i className="ph-fill ph-check-circle text-[11px] text-emerald-500"></i>
                                <span className="text-[11px] font-medium text-slate-400">
                                  {tx.amount_received.toFixed(2)} received
                                </span>
                              </div>
                              {tx.fee_amount > 0 && (
                                <div className="text-[10px] text-slate-500 mt-1">
                                  Fee: {tx.fee_amount.toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </section>
            )}
          </main>
        </div>
      </div>
      <BottomNav />
    </>
  );
}

export default function HistoryPage() {
  return (
    <Suspense fallback={
      <div className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white min-h-screen w-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto"></div>
          <p className="text-slate-400 mt-4">Loading...</p>
        </div>
      </div>
    }>
      <HistoryContent />
    </Suspense>
  );
}